import React, { ChangeEvent, useEffect, useRef, useState } from 'react'

import {
  BorderStyle,
  Box,
  Color,
  FormFieldStatus,
  InputField,
  SelectField,
  Space,
} from '@gousto-internal/citrus-react'
import { Recaptcha } from 'Recaptcha'
import { NullableReCAPTCHA } from 'Recaptcha/useChallengeClosingObserver'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import statusActions from 'actions/status'
import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import { DeliveryEducationBanner } from 'routes/Checkout/Components/Delivery/DeliveryEducationBanner'
import { ErrorMessage } from 'routes/Checkout/Components/ErrorMessage/ErrorMessage'
import { SectionHeader } from 'routes/Checkout/Components/SectionHeader'
import {
  initApplePaySession,
  saveApplePayDeliveryInstruction,
  saveApplePayDeliveryInstructionCustom,
  trackClickPayWithApplePay,
  openLoginModal,
  loginAfterSignup,
} from 'routes/Checkout/checkoutActions'
import { checkoutConstants } from 'routes/Checkout/checkoutConstants'
import {
  getDeliveryTimeAndDate,
  getApplePayRecaptchaExecutionStarted,
  getApplePayDeliveryInstruction,
  getApplePayDeliveryInstructionCustom,
} from 'routes/Checkout/checkoutSelectors'
import { usePricing } from 'routes/Menu/domains/pricing'

import css from './ApplePayButton.css'

const leaveBoxOptions = checkoutConstants.leaveBoxOptions.map((option) => ({
  value: option,
  label: option,
}))

const PayWithApplePay = () => {
  const dispatch = useDispatch()

  const { pricing } = usePricing()

  const { deliveryTime, deliveryDate } = useSelector(getDeliveryTimeAndDate)

  // Form states and change handlers
  const leaveBoxValue = useSelector(getApplePayDeliveryInstruction)
  const [isLeaveBoxValueCorrect, setIsLeaveBoxValueCorrect] = useState(true)

  const customMessage = useSelector(getApplePayDeliveryInstructionCustom)
  const [isCustomMessageCorrect, setIsCustomMessageCorrect] = useState(true)

  const onLeaveBoxChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target
    dispatch(saveApplePayDeliveryInstruction(value))
    setIsLeaveBoxValueCorrect(true)

    if (!checkoutConstants.leaveBoxOptionsWithMessage.includes(value)) {
      dispatch(saveApplePayDeliveryInstructionCustom(''))
      setIsCustomMessageCorrect(true)
    }
  }

  const onCustomMessageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    dispatch(saveApplePayDeliveryInstructionCustom(value))
    setIsCustomMessageCorrect(true)
  }

  // handler for Pay button
  const onPayClick = () => {
    const isValueCorrect = leaveBoxValue !== leaveBoxOptions[0].value
    const isMessageCorrect =
      !checkoutConstants.leaveBoxOptionsWithMessage.includes(leaveBoxValue) ||
      customMessage.length > 4

    setIsLeaveBoxValueCorrect(isValueCorrect)
    setIsCustomMessageCorrect(isMessageCorrect)

    if (isValueCorrect && isMessageCorrect) {
      dispatch({ type: actionTypes.APPLE_PAY_ERRORS_CLEAR })
      dispatch(statusActions.error(actionTypes.CHECKOUT_SIGNUP, null))
      dispatch(statusActions.error(actionTypes.USER_SUBSCRIBE, null))
      dispatch(trackClickPayWithApplePay())
      dispatch(initApplePaySession(pricing, deliveryDate, deliveryTime))
    }
  }

  // ReCAPTCHA related fields
  const recaptchaRef = useRef<NullableReCAPTCHA>(null)
  const [hasGotRecaptchaToken, setHasGotRecaptchaToken] = useState(false)
  const recaptchaExecutionStarted = useSelector(getApplePayRecaptchaExecutionStarted)

  useEffect(() => {
    if (!recaptchaExecutionStarted) return

    if (recaptchaRef.current) recaptchaRef.current.execute()
    // in case feature isRecaptchaEnabled is switched off
    else dispatch(loginAfterSignup())
  }, [dispatch, recaptchaExecutionStarted])

  const onRecaptchaTokenChange = (token: string | null) => {
    // to react only on first reCAPTCHA token (when token is expired, this method is called with null)
    if (hasGotRecaptchaToken) return

    setHasGotRecaptchaToken(true)
    dispatch(loginAfterSignup(token))
  }

  const onRecaptchaChallengeClose = () => {
    if (!hasGotRecaptchaToken) {
      dispatch(statusActions.error(actionTypes.CHECKOUT_SIGNUP_LOGIN, true))
    }
  }

  return (
    <>
      <Box
        borderStyle={BorderStyle.Solid}
        borderWidth={0.5}
        borderColor={Color.ColdGrey_100}
        bg={Color.White}
        paddingH={[3, 6]}
        paddingV={[6, 8]}
        data-testing="checkoutApplePayDeliverySection"
      >
        <SectionHeader title="Delivery details" />

        <Box paddingBottom={6}>
          <SelectField
            id="leaveBox"
            label="Where can we leave your box if you're not in?"
            onChange={onLeaveBoxChange}
            status={isLeaveBoxValueCorrect ? undefined : FormFieldStatus.Error}
            aria-invalid={!isLeaveBoxValueCorrect}
            validationMessage={
              isLeaveBoxValueCorrect ? undefined : 'Delivery instruction is required'
            }
          >
            {leaveBoxOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
                selected={leaveBoxValue === option.value}
              >
                {option.label}
              </option>
            ))}
          </SelectField>

          {checkoutConstants.leaveBoxOptionsWithMessage.includes(leaveBoxValue) && (
            <InputField
              id="customLeaveBox"
              data-testid="customLeaveBox"
              label={undefined}
              placeholder={
                leaveBoxValue === 'Neighbour'
                  ? 'e.g. next door at number 10'
                  : 'e.g. lockbox around left side of house'
              }
              maxLength={32}
              value={customMessage}
              onChange={onCustomMessageChange}
              status={isCustomMessageCorrect ? undefined : FormFieldStatus.Error}
              aria-invalid={!isCustomMessageCorrect}
              validationMessage={
                isCustomMessageCorrect
                  ? undefined
                  : 'Delivery instruction must be at least 5 characters'
              }
            />
          )}
        </Box>

        <DeliveryEducationBanner deliveryTime={deliveryTime} deliveryDate={deliveryDate} />

        <ErrorMessage onLoginClick={(e) => dispatch(openLoginModal(e))} />

        <button className={css.applePayButton} type="submit" onClick={onPayClick}>
          <span className={css.applePayButtonText}>Pay with</span>
          <span className={css.applePayButtonLogo} />
        </button>

        <Recaptcha
          ref={recaptchaRef}
          onChange={onRecaptchaTokenChange}
          onChallengeClose={onRecaptchaChallengeClose}
        />

        <RibbonTriggerContainer name="checkout-apple-pay" />
      </Box>
      <Space size={6} direction="vertical" />
    </>
  )
}

export { PayWithApplePay }
